import { useMutation } from '@apollo/client';
import { ArrowForward, CalendarTodayRounded, DoneOutline, LockOutlined } from '@material-ui/icons';
import { FormikProvider, useFormik } from 'formik';
import {
  ChangePasswordMutation,
  ChangePasswordMutationVariables,
  Role,
  UserUpdateInput,
} from 'lib/generated/graphql';
import { CHANGE_PASSWORD_MUTATION } from 'lib/mutations';
import { GET_VIEWER } from 'lib/queries';
import { DateTime } from 'luxon';
import { useEffect, useRef, useState } from 'react';
import useNotification from 'src/hooks/useNotification';
import { Button, EditButton } from 'src/shared-components';
import useAuth from 'src/shared-components/Auth';
import { InputField } from 'src/shared-components/Form';
import BuilderAvailabilityForm from 'src/shared-components/forms/BuilderAvailability';
import BuilderRateForm from 'src/shared-components/forms/BuilderRate';
import PageContainer from 'src/shared-components/Layout/PageContainer';
import LoadingOverlay from 'src/shared-components/Loading/LoadingOverlay';
import Modal from 'src/shared-components/Modal';
import * as Yup from 'yup';

const FormSetPassword = ({ onFormSubmitted }: { onFormSubmitted: (values: any) => void }) => {
  const form = useFormik({
    initialValues: {
      password: '',
    },
    validationSchema: Yup.object({
      password: Yup.string().min(8).required(),
    }),
    onSubmit: values => {
      onFormSubmitted(values);
    },
  });

  return (
    <FormikProvider value={form}>
      <div className="grid grid-cols-6 gap-4">
        <div className="col-span-6">
          <InputField type="password" name="password" autoComplete="off" placeholder="Password" required />
        </div>
        <div className="col-span-6 mt-2">
          <Button
            data-testid="signInButton"
            size="lg"
            roundness="md"
            color="primary"
            fullWidth
            onClick={() => form.submitForm()}
          >
            Done
          </Button>
        </div>
      </div>
    </FormikProvider>
  );
};

const FormSetPhoneNumber = ({ onFormSubmitted }: { onFormSubmitted: (values: any) => void }) => {
  const form = useFormik({
    initialValues: {
      phoneNumber: '',
    },
    validationSchema: Yup.object({
      phoneNumber: Yup.string().required(),
    }),
    onSubmit: values => {
      onFormSubmitted(values);
    },
  });

  return (
    <FormikProvider value={form}>
      <div className="grid grid-cols-6 gap-4">
        <div className="col-span-6">
          <InputField name="phoneNumber" autoComplete="off" placeholder="Phone Number" required />
        </div>
        <div className="col-span-6 mt-2">
          <Button
            data-testid="signInButton"
            size="lg"
            roundness="md"
            color="primary"
            fullWidth
            onClick={() => form.submitForm()}
          >
            Done
          </Button>
        </div>
      </div>
    </FormikProvider>
  );
};

const FormSetEmail = ({ onFormSubmitted }: { onFormSubmitted: (values: any) => void }) => {
  const form = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().email('Invalid email address').required('Required'),
    }),
    onSubmit: values => {
      onFormSubmitted(values);
    },
  });

  return (
    <FormikProvider value={form}>
      <div className="grid grid-cols-6 gap-4">
        <div className="col-span-6">
          <InputField type="email" name="email" autoComplete="off" placeholder="Email" required />
        </div>
        <div className="col-span-6 mt-2">
          <Button
            data-testid="signInButton"
            size="lg"
            roundness="md"
            color="primary"
            fullWidth
            onClick={() => form.submitForm()}
          >
            Save
          </Button>
        </div>
      </div>
    </FormikProvider>
  );
};

const table = {
  FEW_HOURS_PER_WEEK: 'A few hours per week',
  ONE_DAY_PER_WEEK: '1 day per week',
  TWO_DAYS_PER_WEEK: '2 days per week',
  THREE_DAYS_PER_WEEK: '3 days per week',
  FOUR_DAYS_PER_WEEK: '4 days per week',
  FIVE_DAYS_PER_WEEK: '5 days per week',
  NOT_AVAILABLE_NOW: 'Not available now',
};

const AccountSettings = () => {
  const { user, update } = useAuth();
  const [loading, setIsLoading] = useState(false);
  const { minimumRate, timeAvailable, equityAccepted } = user?.builderOnboarding || {};
  const { showSuccess, showError, hideNotification } = useNotification();
  const [changePasswordMutation] = useMutation<ChangePasswordMutation, ChangePasswordMutationVariables>(
    CHANGE_PASSWORD_MUTATION,
  );
  const [calendlyLink, setCalendlyLink] = useState(user?.calendarLink || '');
  const [isValidURL, setIsValidURL] = useState(false);

  const calendlyPattern = /^https:\/\/calendly\.com\/[\w-]+\/[\w-]+$/;

  const handleInputChange = e => {
    const newValue = e.target.value;
    setCalendlyLink(newValue);
    setIsValidURL(calendlyPattern.test(newValue));
  };

  useEffect(() => {
    if (user?.calendarLink) setIsValidURL(calendlyPattern.test(user.calendarLink));
  }, [user?.calendarLink]);

  const updatePasswordModalRef = useRef<any>();
  const updateEmailModalRef = useRef<any>();

  const openPasswordChangeModal = () => {
    updatePasswordModalRef.current.open();
  };

  const openEmailChangeModal = () => {
    updateEmailModalRef.current.open();
  };

  const builderRateRef = useRef<any>();
  const builderAvailabilityRef = useRef<any>();

  const updatePhoneNumberRef = useRef<any>();
  const openPhoneNumberModal = () => {
    updatePhoneNumberRef.current.open();
  };

  const handlePasswordUpdate = async (values: any) => {
    try {
      await changePasswordMutation({
        variables: {
          input: values.password,
        },
      });
      showSuccess({ title: 'Password updated', description: 'Your password was successfully updated' });
      updatePasswordModalRef.current.close();
    } catch (error: any) {
      showError({ title: 'Cannot update password', description: error.message });
    }
  };

  const handlePhoneNumberUpdate = async ({ phoneNumber }: { phoneNumber: string }) => {
    try {
      const updateData: UserUpdateInput = {
        phoneNumber,
      };
      await update(updateData);
      updatePhoneNumberRef.current.close();
    } catch (error: any) {
      console.error(error);
    }
  };

  const handleEmailUpdate = async ({ email }: { email: string }) => {
    try {
      const updateData: UserUpdateInput = {
        email,
      };
      await update(updateData);
      updateEmailModalRef.current.close();
    } catch (error: any) {
      console.error(error);
    }
  };

  const builderRateSubmit = async (values: any) => {
    try {
      const { minimumRate } = values;
      const updateData: UserUpdateInput = {
        builderOnboarding: {
          update: {
            minimumRate: { set: minimumRate },
          },
        },
      };
      await update(updateData);
      builderRateRef.current.close();
    } catch (error: any) {
      console.error(error);
    }
  };

  const BuilderAvailabilitySubmit = async (values: any) => {
    try {
      const { timeAvailable, equityAccepted } = values;
      const updateData: UserUpdateInput = {
        builderOnboarding: {
          update: {
            timeAvailable: { set: timeAvailable },
            equityAccepted: { set: equityAccepted },
          },
        },
      };
      await update(updateData);
      builderAvailabilityRef.current.close();
    } catch (error: any) {
      console.error(error);
    }
  };

  const calendlyAuth = async (authNeeded: boolean) => {
    setIsLoading(true);
    const updateData: UserUpdateInput = {
      calendarLink: { set: calendlyLink },
    };
    await update(updateData);
    setIsLoading(false);

    const authorizationUrl = `https://auth.calendly.com/oauth/authorize?client_id=${process.env.CALENDLY_CLIENT_ID}&response_type=code&redirect_uri=${process.env.API_BASE_URL}/calendly/callback&state=${user?.id}`;

    if (authNeeded) window.location.href = authorizationUrl;
    if (!authNeeded) showSuccess({ title: 'Saved', description: 'Your changes have been saved' });
  };

  const revokeCalendly = async () => {
    setIsLoading(true);
    const updateData: UserUpdateInput = {
      calendlyConnected: { set: false },
      calendlyAccess: { set: null },
      calendlyRefresh: { set: null },
    };
    await update(updateData);
    setIsLoading(false);
  };

  // TODO: Improve this, modify the provider in order to not have to put this in the component
  useEffect(() => {
    return () => {
      hideNotification();
    };
  }, []);

  return (
    <PageContainer
      tabs={
        user?.role === Role.Customer
          ? [
              { label: 'Settings', href: '/account/settings' },
              { label: 'Banking', href: '/account/settings/banking' },
            ]
          : null
      }
      title={user?.role === Role.Builder ? 'Settings' : null}
    >
      <div>
        {user?.role === Role.Builder && (
          <>
            <div className="pb-3 text-[#151515] uppercase text-sm font-bold">Preferences</div>
            <div className="max-w-[915px] bg-white shadow rounded-lg py-8 px-10 mb-8">
              <div className={`flex flex-row gap-x-16`}>
                <div className="">
                  <div className="pb-3 text-gray-400 uppercase font-bold">what you make</div>
                  <div className="flex items-center">
                    <div className="font-medium text-[21px] flex">
                      <p>$ {minimumRate || 0}/hr </p>
                      <EditButton onClick={() => builderRateRef?.current?.open()} />
                    </div>
                  </div>
                </div>
                <div className="">
                  <div className="pb-3 text-gray-400 uppercase font-bold">Your huddle rate</div>
                  <div className="flex items-center">
                    <div className="font-medium text-[21px]">$ {Math.round(minimumRate * 1.25) || 0}/hr</div>
                  </div>
                </div>
              </div>
            </div>

            <div className={`max-w-[915px] bg-white shadow rounded-lg py-8 px-6 mb-8 relative`}>
              {!user?.isTeamLead && (
                <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
                  <LockOutlined className="text-[#A8A8A8]" style={{ fontSize: '64px' }} />
                  <div className="font-medium text-xl">
                    Must be a{' '}
                    <a
                      href="https://lu.ma/4gjd0a9s"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:opacity-60"
                    >
                      Lead
                    </a>{' '}
                    to access.
                  </div>
                </div>
              )}
              <div className={`flex ${!user?.isTeamLead ? 'opacity-10 pointer-events-none' : ''}`}>
                <div className="bg-[#DEEBE9] h-fit p-3 rounded-full mr-6">
                  {user?.calendlyConnected ? (
                    <DoneOutline fontSize="medium" className="" aria-hidden="true" />
                  ) : (
                    <CalendarTodayRounded fontSize="medium" className="" aria-hidden="true" />
                  )}
                </div>
                <div className="w-full">
                  <div className="text-[26px] font-medium">
                    {user?.calendlyConnected
                      ? 'You’re available for 1-hr booking.'
                      : 'Make yourself available for 1-hr booking.'}
                  </div>
                  <div className="text-[#797979] font-medium">
                    {user?.calendlyConnected
                      ? `${process.env.NEXT_PUBLIC_APP_URL}/book/${user?.username} is live.`
                      : 'Create a 1-hour long Calendly event and add it to Huddle.'}
                  </div>
                  {
                    <div className="mt-8 flex flex-col gap-y-2 w-full">
                      <div>
                        {user?.calendlyConnected ? (
                          <p>
                            Update your&nbsp;
                            <a
                              href="https://calendly.com/event_types/user/me"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:underline cursor-pointer"
                            >
                              Calendly
                            </a>
                            &nbsp;link if needed.
                          </p>
                        ) : (
                          <p>
                            Paste the full link for your 1-hour event on&nbsp;
                            <a
                              href="https://calendly.com/event_types/user/me"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:underline cursor-pointer"
                            >
                              Calendly.
                            </a>
                          </p>
                        )}
                      </div>
                      <div className={`flex flex-col mb-2`}>
                        <input
                          className={`text-[17px] rounded-lg bg-[#F3F3F3] p-4 w-5/6 ${
                            user?.calendlyConnected ? '' : 'mb-2'
                          }`}
                          placeholder="https://calendly.com/{username}/{eventname}"
                          value={calendlyLink}
                          onChange={handleInputChange}
                        />
                        {user?.calendlyConnected ? (
                          <div className="flex gap-x-4">
                            <button
                              className={`bg-[#364442] w-fit py-4 px-6 mt-2 text-white rounded-lg font-medium ${
                                !isValidURL || loading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                              }`}
                              disabled={!isValidURL || loading}
                              onClick={() => calendlyAuth(false)}
                            >
                              {loading ? 'Updating...' : 'Update Calendly'}
                            </button>
                            <button
                              className={`bg-[#800000] w-fit py-4 px-6 mt-2 text-white rounded-lg font-medium ${
                                loading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                              }`}
                              disabled={loading}
                              onClick={() => revokeCalendly()}
                            >
                              Revoke Calendly
                            </button>
                          </div>
                        ) : (
                          <button
                            className={`bg-[#364442] w-fit py-4 px-6 mt-2 text-white rounded-lg font-medium ${
                              !isValidURL || loading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                            }`}
                            disabled={!isValidURL || loading}
                            onClick={() => calendlyAuth(true)}
                          >
                            Authenticate Calendly
                          </button>
                        )}
                      </div>
                    </div>
                  }
                </div>
              </div>
            </div>

            <div className="pb-3 text-[#151515] uppercase text-sm font-bold">Availability</div>
            <div className="max-w-[915px] bg-white shadow rounded-lg py-8 px-10 mb-8">
              <div className={`flex flex-row gap-x-16`}>
                <div className="">
                  <div className="pb-3 text-gray-400 uppercase text-sm font-bold">current availability</div>
                  <div className="flex items-center">
                    <div className="font-medium text-[21px]">
                      {timeAvailable ? table[timeAvailable] : 'Set your current availability'}
                    </div>
                  </div>
                </div>
                <div className="">
                  <div className="pb-3 text-gray-400 uppercase text-sm font-bold">Open to equity projects</div>
                  <div className="flex items-center">
                    <div className="font-medium text-[21px]">{equityAccepted ? 'Yes' : 'No'}</div>
                  </div>
                </div>
                <Button
                  className="ml-auto px-12 py-6 hidden md:flex"
                  size="lg"
                  color="deep-black"
                  onClick={() => builderAvailabilityRef?.current?.open()}
                >
                  Update
                </Button>
              </div>
              <Button
                className="ml-auto px-12 py-6 md:hidden mt-8"
                size="lg"
                color="deep-black"
                onClick={() => builderAvailabilityRef?.current?.open()}
              >
                Update
              </Button>
            </div>
          </>
        )}
        {user?.role === Role.Customer && (
          <>
            {/* Subscriptions section temporarily hidden as HuddleUp was the only subscription
            <div className="pb-3 text-[#151515] uppercase text-sm font-bold">Subscriptions</div>
            <div className="mb-8 w-full max-w-[915px] rounded-2xl py-4 px-8 bg-white shadow">
              <div className="text-[17px] flex w-full items-center">
                <div className="opacity-60">No active subscriptions</div>
              </div>
            </div>
            */}
          </>
        )}
        <div className="pb-3 text-[#151515] uppercase text-sm font-bold">Account settings</div>
        <div className={`${user?.role === Role.Builder && 'max-w-[915px]'} bg-white shadow rounded-lg pt-8 px-10 mb-8`}>
          <div className="pb-8">
            <div className="pb-3 text-gray-400 uppercase text-sm font-bold">email address</div>
            <div className="flex items-center mb-3 sm:mb-0">
              <div className="mr-6 my-auto font-medium text-[21px] ">{user?.email}</div>
              <EditButton onClick={openEmailChangeModal} />
            </div>
          </div>
          <div className="pb-8">
            <div className="pb-3 text-gray-400 uppercase text-sm font-bold">username</div>
            <div className="font-medium text-[21px]">{user?.username}</div>
          </div>
          <div className="pb-8">
            <div className="pb-3 text-gray-400 uppercase text-sm font-bold">password</div>
            <div className="flex items-center">
              <div className="font-medium text-[21px]">********</div>
              <EditButton onClick={openPasswordChangeModal} />
            </div>
          </div>
          <div className="pb-8">
            <div className="pb-3 text-gray-400 uppercase text-sm font-bold">phone number</div>
            <div className="flex items-center">
              <div className="font-medium text-[21px]">{user?.phoneNumber ? user?.phoneNumber : 'No Phone Number'}</div>
              <EditButton onClick={openPhoneNumberModal} />
            </div>
          </div>
        </div>
      </div>
      <Modal showCloseButton ref={updatePasswordModalRef}>
        <div>
          <h1 className="text-2xl font-bold text-center">Change your password</h1>
          <p className="py-4 text-center text-gray-400">Enter the new password you would like to use.</p>
          <FormSetPassword onFormSubmitted={handlePasswordUpdate} />
        </div>
      </Modal>
      <Modal showCloseButton ref={updatePhoneNumberRef}>
        <div>
          <h1 className="text-2xl font-bold text-center">{user?.phoneNumber ? 'Change' : 'Enter'} your phone number</h1>
          <p className="py-4 text-center text-gray-400">
            {user?.phoneNumber ? 'Change' : 'Enter'} the new phone number you would like to use.
          </p>
          <FormSetPhoneNumber onFormSubmitted={handlePhoneNumberUpdate} />
        </div>
      </Modal>
      <Modal showCloseButton ref={updateEmailModalRef}>
        Change Email for {user?.name} from {user?.email} to:
        <FormSetEmail onFormSubmitted={handleEmailUpdate} />
      </Modal>
      <Modal showCloseButton ref={builderRateRef}>
        <div className="max-w-md">
          <h1 className="text-2xl font-bold text-center">Set minimum rate</h1>
          <p className="py-4 text-center text-gray-400">
            These defaults are only visible in your Settings. You can also specify custom rates per project.
          </p>
          <BuilderRateForm submitForm={builderRateSubmit} minimumRate={minimumRate} />
        </div>
      </Modal>
      <Modal showCloseButton ref={builderAvailabilityRef}>
        <div className="max-w-md">
          <h1 className="text-2xl font-bold text-center">Set availability and equity</h1>
          <p className="py-4 text-center text-gray-400">
            These defaults are only visible in your Settings. You can also specify custom availability and equity pay
            per project.
          </p>
          <BuilderAvailabilityForm
            submitForm={BuilderAvailabilitySubmit}
            timeAvailable={timeAvailable}
            equityAccepted={equityAccepted}
          />
        </div>
      </Modal>
    </PageContainer>
  );
};

export default AccountSettings;
